import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { Row, Col, Statistic, Spin, Tag, Badge, List, Empty, Tooltip, Button, message, Space } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  TeamOutlined,
  DollarOutlined,
  BankOutlined,
  MessageOutlined,
  BulbOutlined,
  ReadOutlined,
  CommentOutlined,
  ShopOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RobotOutlined,
  FireFilled,
  WarningOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import styles from './Admin.module.css';

const HOUSE_COLORS = {
  Gryffindor: '#740001',
  Slytherin: '#1a472a',
  Hufflepuff: '#f0ad4e',
  Ravenclaw: '#0e1a40'
};

const HOUSE_LABELS_FA = {
  Gryffindor: 'گریفیندور',
  Slytherin: 'اسلیترین',
  Hufflepuff: 'هافلپاف',
  Ravenclaw: 'ریونکلاو'
};

const ACTIVITY_ICONS = {
  user: <UserOutlined style={{ color: '#740001' }} />,
  payment: <DollarOutlined style={{ color: '#1a7f37' }} />,
  ticket: <MailOutlined style={{ color: '#1890ff' }} />,
  contact: <MessageOutlined style={{ color: '#9b59b6' }} />,
  feature: <BulbOutlined style={{ color: '#c07f11' }} />
};

const AUTO_REFRESH_MS = 30000;

const SECURITY_TYPE_LABELS_FA = {
  failed_login: 'ورود ناموفق',
  admin_failed_login: 'ورود ناموفق ادمین',
  otp_failed: 'کد OTP اشتباه',
  brute_force: 'brute-force',
  banned_access_attempt: 'دسترسی کاربر مسدود',
  payment_anomaly: 'ناهنجاری پرداخت',
  multi_account_attempt: 'تلاش چندحسابی (Sybil)'
};
const SECURITY_SEVERITY_COLORS = { low: 'default', medium: 'gold', high: 'red' };

function toman(n) {
  return `${Number(n || 0).toLocaleString('fa-IR')} تومان`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'همین الان';
  if (min < 60) return `${min} دقیقه پیش`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} ساعت پیش`;
  const day = Math.floor(hr / 24);
  return `${day} روز پیش`;
}

function formatUptime(seconds) {
  const s = Number(seconds) || 0;
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days} روز و ${hours} ساعت`;
  if (hours > 0) return `${hours} ساعت و ${mins} دقیقه`;
  return `${mins} دقیقه`;
}

/**
 * نمودار خط کوچک (sparkline) کاملاً سبک — بدون هیچ وابستگی خارجی، چون امکان
 * npm install در این نشست وجود نداشت. برای ۱۴ نقطه‌ی روزانه کافی و سریع است.
 */
function MiniTrendChart({ data, valueKey = 'count', color = '#740001', suffix = '' }) {
  const width = 300;
  const height = 70;
  const padding = 6;

  if (!data || data.length === 0) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>—</div>;
  }

  const values = data.map(d => Number(d[valueKey]) || 0);
  const max = Math.max(...values, 1);
  const step = (width - padding * 2) / Math.max(values.length - 1, 1);

  const points = values.map((v, i) => {
    const x = padding + i * step;
    const y = height - padding - (v / max) * (height - padding * 2);
    return [x, y];
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${height - padding} L${points[0][0].toFixed(1)},${height - padding} Z`;

  const total = values.reduce((a, b) => a + b, 0);
  const last = values[values.length - 1];

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height, display: 'block' }} preserveAspectRatio="none">
        <path d={areaPath} fill={color} opacity="0.10" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r={i === points.length - 1 ? 3.5 : 0} fill={color} />
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 12, color: '#888' }}>
        <span>مجموع ۱۴ روز: <b style={{ color: '#333' }}>{total.toLocaleString('fa-IR')}{suffix}</b></span>
        <span>امروز تا الان: <b style={{ color }}>{last.toLocaleString('fa-IR')}{suffix}</b></span>
      </div>
    </div>
  );
}

function KpiCard({ icon, iconBg, iconColor, title, value, suffix, extra, borderColor }) {
  return (
    <ProCard bordered={false} className={styles.statsCard} style={{ borderTop: `4px solid ${borderColor}` }}>
      <div className={styles.statIcon} style={{ background: iconBg, color: iconColor }}>{icon}</div>
      <Statistic title={title} value={value} suffix={suffix} valueStyle={{ color: borderColor, fontWeight: 700 }} />
      {extra}
    </ProCard>
  );
}

function HealthBadge({ ok, okText, badText, extra }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: ok ? 'rgba(82,196,26,0.08)' : 'rgba(255,77,79,0.08)' }}>
      {ok ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} /> : <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />}
      <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{ok ? okText : badText}</span>
      {extra && <span style={{ marginInlineStart: 'auto', color: '#888', fontSize: 12 }}>{extra}</span>}
    </div>
  );
}

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await adminAPI.getDashboardStats();
      if (response.data.success) {
        setStats(response.data.stats);
        setLastUpdated(new Date());
      }
    } catch (error) {
      message.error('خطا در بارگذاری آمار داشبورد');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), AUTO_REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" tip="در حال بارگذاری وضعیت کلی..." />
      </div>
    );
  }

  if (!stats) return <Empty description="آماری موجود نیست" />;

  const houseEntries = Object.entries(stats.users?.byHouse || {}).filter(([h]) => h !== 'null');
  const da = stats.diagonAlley || {};
  const sys = stats.system || {};

  return (
    <div>
      {/* ── Header row: refresh + last update ─────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginBottom: 16 }}>
        {lastUpdated && (
          <span style={{ fontSize: 12, color: '#999' }}>
            <ClockCircleOutlined style={{ marginInlineEnd: 4 }} />
            آخرین به‌روزرسانی: {timeAgo(lastUpdated)}
          </span>
        )}
        <Tooltip title="به‌روزرسانی دستی">
          <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={() => load(false)}>
            به‌روزرسانی
          </Button>
        </Tooltip>
      </div>

      {/* ── Top KPI cards ──────────────────────────────────────────── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<UserOutlined />} iconBg="rgba(116,0,1,0.10)" iconColor="#740001"
            title="کل کاربران" value={stats.users?.total ?? stats.totalUsers}
            borderColor="#740001"
            extra={<Tag style={{ marginTop: 8 }} color="volcano">+{stats.users?.newToday ?? 0} امروز</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<span>⚡</span>} iconBg="rgba(155,89,182,0.13)" iconColor="#9b59b6"
            title="جادوگران" value={stats.users?.wizards ?? stats.totalWizards}
            borderColor="#9b59b6"
            extra={<Tag style={{ marginTop: 8 }} color="purple">+{stats.users?.newThisWeek ?? 0} این هفته</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<DollarOutlined />} iconBg="rgba(24,144,255,0.13)" iconColor="#1890ff"
            title="درآمد کل (تومان)" value={(stats.payments?.totalRevenueToman || 0).toLocaleString('fa-IR')}
            borderColor="#1890ff"
            extra={<Tag style={{ marginTop: 8 }} color="blue">امروز: {toman(stats.payments?.todayRevenueToman)}</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<BankOutlined />} iconBg="rgba(240,173,78,0.17)" iconColor="#c07f11"
            title="گالیون در گرینگوتز" value={stats.bank?.totalGalleons ?? 0}
            borderColor="#c07f11"
            extra={<Tag style={{ marginTop: 8 }} color="gold">{stats.bank?.totalVaults ?? 0} صندوق ({stats.bank?.lockedVaults ?? 0} قفل)</Tag>}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 4 }}>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<MailOutlined />} iconBg="rgba(24,144,255,0.13)" iconColor="#1890ff"
            title="تیکت‌های باز" value={stats.support?.openTickets ?? 0}
            borderColor="#1890ff"
            extra={<Tag style={{ marginTop: 8 }} color={stats.support?.unreadContactMessages ? 'red' : 'default'}>{stats.support?.unreadContactMessages ?? 0} پیام تماس نخوانده</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<BulbOutlined />} iconBg="rgba(240,173,78,0.17)" iconColor="#c07f11"
            title="درخواست ویژگی در انتظار" value={stats.support?.pendingFeatureRequests ?? 0}
            borderColor="#c07f11"
            extra={<Tag style={{ marginTop: 8 }}>{stats.support?.totalFeatureRequests ?? 0} مجموع</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<ReadOutlined />} iconBg="rgba(114,47,55,0.11)" iconColor="#722f37"
            title="مقالات در انتظار تایید" value={stats.content?.blogPending ?? 0}
            borderColor="#722f37"
            extra={<Tag style={{ marginTop: 8 }} color="green">{stats.content?.blogApproved ?? 0} منتشرشده</Tag>}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiCard
            icon={<RobotOutlined />} iconBg="rgba(24,144,255,0.13)" iconColor="#1890ff"
            title="گفتگو با دامبلدور" value={stats.chat?.totalConversations ?? 0}
            borderColor="#1890ff"
            extra={<Tag style={{ marginTop: 8 }}>+{stats.chat?.conversationsToday ?? 0} امروز</Tag>}
          />
        </Col>
      </Row>

      {/* ── Trends ─────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={12}>
          <ProCard title="روند ثبت‌نام (۱۴ روز اخیر)" headerBordered className={styles.statsCard}>
            <MiniTrendChart data={stats.users?.signupTrend} valueKey="count" color="#740001" />
          </ProCard>
        </Col>
        <Col xs={24} lg={12}>
          <ProCard title="روند درآمد (۱۴ روز اخیر)" headerBordered className={styles.statsCard}>
            <MiniTrendChart data={stats.payments?.revenueTrend} valueKey="amount" color="#1890ff" suffix=" ت" />
          </ProCard>
        </Col>
      </Row>

      {/* ── House distribution ─────────────────────────────────────── */}
      {houseEntries.length > 0 && (
        <ProCard title="توزیع خانه‌ها" headerBordered className={styles.statsCard} style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            {houseEntries.map(([house, count]) => (
              <Col xs={12} sm={6} key={house}>
                <ProCard size="small" bordered className={styles[`house${house}`]} style={{ textAlign: 'center' }}>
                  <Statistic
                    title={HOUSE_LABELS_FA[house] || house}
                    value={count}
                    valueStyle={{ color: HOUSE_COLORS[house] || '#666' }}
                  />
                </ProCard>
              </Col>
            ))}
          </Row>
        </ProCard>
      )}

      {/* ── Diagon Alley + System health ───────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={14}>
          <ProCard
            title={<span><ShopOutlined style={{ marginInlineEnd: 6 }} />کوچه دیاگون (اقتصاد بازی)</span>}
            headerBordered
            className={styles.statsCard}
            extra={<Badge status={da.reachable ? 'success' : 'error'} text={da.reachable ? `متصل (${da.latencyMs}ms)` : 'در دسترس نیست'} />}
          >
            {da.reachable ? (
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8}><Statistic title="سفارش‌ها" value={da.orders ?? 0} /></Col>
                <Col xs={12} sm={8}><Statistic title="محصولات" value={da.products ?? 0} /></Col>
                <Col xs={12} sm={8}><Statistic title="فروشگاه‌ها" value={da.shops ?? 0} /></Col>
                <Col xs={12} sm={8}><Statistic title="صندوق‌ها" value={da.vaults ?? 0} /></Col>
                <Col xs={12} sm={8}><Statistic title="پروفایل‌ها" value={da.profiles ?? 0} /></Col>
                <Col xs={12} sm={8}><Statistic title="درآمد (گالیون)" value={da.totalRevenueGalleons ?? 0} valueStyle={{ color: '#c07f11' }} /></Col>
              </Row>
            ) : (
              <Empty description={`ارتباط با کوچه دیاگون برقرار نشد${da.error ? ` (${da.error})` : ''}`} />
            )}
          </ProCard>
        </Col>

        <Col xs={24} lg={10}>
          <ProCard title="سلامت سیستم" headerBordered className={styles.statsCard}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <HealthBadge ok okText="بک‌اند اصلی: آنلاین" extra={`آپ‌تایم ${formatUptime(sys.uptimeSeconds)}`} />
              <HealthBadge ok={sys.dbConnected} okText="پایگاه‌داده: متصل" badText="پایگاه‌داده: قطع" />
              <HealthBadge ok={da.reachable} okText="کوچه دیاگون: آنلاین" badText="کوچه دیاگون: آفلاین" extra={da.reachable ? `${da.latencyMs}ms` : ''} />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                محیط: {sys.nodeEnv === 'production' ? 'تولید' : 'توسعه'} · حافظه: {sys.memoryRssMb} مگابایت
              </div>
            </div>
          </ProCard>
        </Col>
      </Row>

      {/* ── Waitlist (kept from previous dashboard) ───────────────── */}
      <ProCard title="لیست انتظار" headerBordered className={`${styles.statsCard} ${styles.waitlistCard}`} style={{ marginTop: 20 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div className={`${styles.statIcon} ${styles.statIconWaitlist}`}><TeamOutlined /></div>
            <Statistic title="مجموع کل" value={stats.waitlistTotal ?? 0} valueStyle={{ color: '#722f37', fontWeight: 'bold' }} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="فارسی (ایران)" value={stats.waitlistFa ?? 0} valueStyle={{ color: '#1890ff' }} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="English" value={stats.waitlistEn ?? 0} valueStyle={{ color: '#52c41a' }} />
          </Col>
        </Row>
      </ProCard>

      {/* ── Security alerts ────────────────────────────────────────── */}
      {(() => {
        const sec = stats.security || {};
        const bySeverity = sec.last24hBySeverity || {};
        const total24h = (bySeverity.low || 0) + (bySeverity.medium || 0) + (bySeverity.high || 0);
        const hasHighUnread = (sec.recent || []).some(r => r.severity === 'high' && !r.isRead);
        return (
          <ProCard
            title={<span><WarningOutlined style={{ color: hasHighUnread ? '#ff4d4f' : '#f0ad4e', marginInlineEnd: 6 }} />فعالیت‌های مشکوک</span>}
            headerBordered
            className={styles.statsCard}
            style={{ marginTop: 20, ...(hasHighUnread ? { borderTop: '4px solid #ff4d4f' } : {}) }}
            extra={
              <Space>
                <Tag color={sec.unreadCount ? 'red' : 'default'}>بررسی‌نشده: {sec.unreadCount ?? 0}</Tag>
                <Tag>۲۴ ساعت اخیر: {total24h}</Tag>
                {!!bySeverity.high && <Tag color="red">شدید: {bySeverity.high}</Tag>}
                {!!bySeverity.medium && <Tag color="gold">متوسط: {bySeverity.medium}</Tag>}
              </Space>
            }
          >
            {sec.recent && sec.recent.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={sec.recent}
                renderItem={(item) => (
                  <List.Item key={item._id}>
                    <List.Item.Meta
                      avatar={<SafetyOutlined style={{ color: SECURITY_SEVERITY_COLORS[item.severity] === 'red' ? '#ff4d4f' : '#f0ad4e' }} />}
                      title={<span>{item.message} <Tag color={SECURITY_SEVERITY_COLORS[item.severity]} style={{ marginInlineStart: 6 }}>{SECURITY_TYPE_LABELS_FA[item.type] || item.type}</Tag></span>}
                      description={item.ip ? `IP: ${item.ip}` : null}
                    />
                    <span style={{ color: '#aaa', fontSize: 12, whiteSpace: 'nowrap' }}>{timeAgo(item.createdAt)}</span>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="فعالیت مشکوک متوسط/شدیدی ثبت نشده" />
            )}
          </ProCard>
        );
      })()}

      {/* ── Activity feed ──────────────────────────────────────────── */}
      <ProCard
        title={<span><FireFilled style={{ color: '#f0ad4e', marginInlineEnd: 6 }} />فعالیت اخیر</span>}
        headerBordered
        className={styles.statsCard}
        style={{ marginTop: 20 }}
      >
        {stats.activity && stats.activity.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={stats.activity}
            renderItem={(item, idx) => (
              <List.Item key={idx}>
                <List.Item.Meta
                  avatar={ACTIVITY_ICONS[item.type] || <CommentOutlined />}
                  title={item.title}
                  description={item.detail}
                />
                <span style={{ color: '#aaa', fontSize: 12, whiteSpace: 'nowrap' }}>{timeAgo(item.at)}</span>
              </List.Item>
            )}
          />
        ) : (
          <Empty description="هنوز فعالیتی ثبت نشده" />
        )}
      </ProCard>
    </div>
  );
}
